import { StorageClientImpl } from "../../api-polyfill/src/storage";
import { toGoLang, AsyncConstructors, asyncFlatMap, asyncForEach, workspaceToLayout, safeInteropRegister, layoutToWorkspace, removeDups } from "../../api-polyfill/src/common/windowFunctions";
export function camelCase(...args) {
    let str = "";
    for (let i = 0; i < args.length; i++) {
        str = `${str} ${args[i]}`;
    }
    return str
        .replace(/\s(.)/g, ($1) => $1.toUpperCase())
        .replace(/\s/g, "")
        .replace(/^(.)/, ($1) => $1.toLowerCase());
}
export class DataMigration {
    constructor(glue, manifest, logger) {
        this.glue = glue;
        this.manifest = manifest;
        this.logger = logger;
        this.storageClient = null;
    }
    async fillInWindowData(storageClient, workspace) {
        const datums = await asyncFlatMap(workspace?.windows ?? [], async (windowName) => {
            const windowDataKey = camelCase(workspace.name, windowName);
            const componentDataKey = camelCase(workspace.name, windowName, windowName);
            const { data: windowData } = await storageClient.getStandardized({ key: windowDataKey, topic: "finsemble.workspace" });
            const { data: componentStates } = await storageClient.getStandardized({ key: componentDataKey, topic: "finsemble.workspace" });
            return [{ windowName, windowData, componentStates }];
        });
        workspace.componentStates = Object.fromEntries(datums.map((data) => [data.windowName, data.componentStates]));
        workspace.windowData = datums.map((data) => {
            return {
                ...data.windowData
            };
        });
    }
    async migrateWorkspaces() {
        const storageClient = this.storageClient;
        const { err, data: workspaceNames } = await storageClient.getStandardized({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace" });
        this.logger.debug("data-migration - workspaceNames=", workspaceNames, err);
        const keys = (workspaceNames ?? []).flatMap((workspaceName) => workspaceName ? [camelCase(workspaceName)] : []);
        const workspaces = await asyncFlatMap(keys, async (key) => {
            const { data } = await storageClient.getStandardized({ key, topic: "finsemble.workspace" });
            return data ? [data] : [];
        });
        await asyncForEach(workspaces, async (workspace) => {
            await this.fillInWindowData(storageClient, workspace);
        });
        this.logger.debug("data-migration - workspaces from storage adapter", workspaces);
        const oldLayouts = await this.glue.layouts.getAll("Global");
        this.logger.debug("data-migration - removing existing iocd layouts", oldLayouts.map((layout) => layout.name));
        for (let i = 0; i < oldLayouts.length; i++) {
            await toGoLang(() => this.glue.layouts.remove("Global", oldLayouts[i].name));
        }
        const newLayouts = workspaces.map((workspace, index) => workspaceToLayout(workspace.name, workspace, index));
        this.logger.debug("data-migration - converted layouts", newLayouts);
        const { err: err2, data: glueImportResult } = await toGoLang(() => this.glue.layouts.import(newLayouts, "merge"));
        if (err2 || !glueImportResult) {
            const error = `data-migration - Error importing converted layouts ${err2}`;
            this.logger.error(error);
        }
        const { data } = await storageClient.getStandardized({ key: "finsemble.lastUsedWorkspace.name", topic: "finsemble.workspace" });
        if (data) {
            this.logger.debug("data-migration - setting current workspace from lastUsedWorkspace.name", data);
            await toGoLang(() => this.glue.prefs.setFor("IO.Global.FinsembleLastWorkspace", { name: data }));
        }
    }
    async migratePreferences() {
        const storageClient = this.storageClient;
        const { data: oldUserPreferences } = await storageClient.getStandardized({ key: "userPreferences", topic: "finsemble" });
        this.logger.debug("data-migration - Preferences from Finsemble storage adapter", oldUserPreferences);
        const { err: errClear } = await toGoLang(() => this.glue.prefs.clearFor("IO.Global.FinsemblePreferences"));
        if (errClear) {
            this.logger.error("data-migration - error clearing preferences IO.Global.FinsemblePreferences", errClear);
        }
        if (oldUserPreferences) {
            const { err } = await toGoLang(() => this.glue.prefs.updateFor("IO.Global.FinsemblePreferences", oldUserPreferences));
            if (err) {
                this.logger.error("data-migration - error saving preferences IO.Global.FinsemblePreferences", err);
            }
        }
        else {
            this.logger.debug("data-migration - No user preferences found in Finsemble storage adapter. IO.Global.FinsemblePreferences will be empty.");
        }
    }
    async migrateAdvancedAppLauncher() {
        const storageClient = this.storageClient;
        const { data: oldUserLauncherStore } = await storageClient.getStandardized({ key: "distributedStore-Finsemble-AppLauncher-Store", topic: "finsemble" });
        const values = oldUserLauncherStore?.values;
        if (!values) {
            return;
        }
        this.logger.debug("data-migration - advanced app launcher settings from Finsemble storage adapter", values);
        const convertedToPrefs = {
            activeFolder: values.activeFolder,
            activeTags: values.activeTags,
            dragDisabled: [
                "Advanced App Launcher",
                "Favorites"
            ],
            folders: values.appFolders.folders,
            foldersList: values.appFolders.list,
            sortBy: values.sortBy
        };
        this.glue.prefs.setFor("IO.Global.Launcher", convertedToPrefs);
    }
    async maybeMigrateWorkspaces(workspacesLoad, oneTimeConversionStatus) {
        if (workspacesLoad === "iocd") {
            this.logger.debug("data-migration - Skipping data migration of workspaces");
        }
        else if (workspacesLoad === "oneTimeConversion" && oneTimeConversionStatus?.workspaces !== true) {
            this.logger.debug(`data-migration - Migrating workspaces ${workspacesLoad}`);
            await this.migrateWorkspaces();
            await this.glue.prefs.updateFor("IO.Global.FinsembleOneTimeConversion", { workspaces: true });
            localStorage.setItem("oneTimeConversion:workspaces", "true");
        }
        else {
            this.logger.debug(`data-migration - Migrating workspaces ${workspacesLoad}`);
            await this.migrateWorkspaces();
        }
    }
    async maybeMigratePreferences(preferencesLoad, oneTimeConversionStatus) {
        if (preferencesLoad === "iocd") {
            this.logger.debug("data-migration - Skipping data migration of preferences");
        }
        else if (preferencesLoad === "oneTimeConversion" && oneTimeConversionStatus?.preferences !== true) {
            this.logger.debug(`data-migration - Migrating preferences ${preferencesLoad}`);
            await this.migratePreferences();
            await this.glue.prefs.updateFor("IO.Global.FinsembleOneTimeConversion", { preferences: true });
        }
        else {
            this.logger.debug(`data-migration - Migrating preferences ${preferencesLoad}`);
            await this.migratePreferences();
        }
    }
    async maybeMigrateAdvancedAppLauncher(advancedAppLauncherLoad, oneTimeConversionStatus) {
        if (advancedAppLauncherLoad === "iocd") {
            this.logger.debug("data-migration - Skipping data migration of advanced app launcher");
        }
        else if (advancedAppLauncherLoad === "oneTimeConversion" && oneTimeConversionStatus?.advancedAppLauncher !== true) {
            this.logger.debug(`data-migration - Migrating preferences ${advancedAppLauncherLoad}`);
            await this.migrateAdvancedAppLauncher();
            await this.glue.prefs.updateFor("IO.Global.FinsembleOneTimeConversion", { advancedAppLauncher: true });
        }
        else {
            this.logger.debug(`data-migration - Migrating advancedAppLauncher ${advancedAppLauncherLoad}`);
            await this.migrateAdvancedAppLauncher();
        }
    }
    async setLastUsedWorkspaceName() {
        const storageClient = this.storageClient;
        const layout = await this.glue.layouts.getCurrentLayout();
        if (layout) {
            await storageClient.save({ key: "finsemble.lastUsedWorkspace.name", topic: "finsemble.workspace", value: layout.name });
        }
    }
    async saveToStorageAdapter(workspaces) {
        const storageClient = this.storageClient;
        this.logger.debug("data-migration - Saving workspaces to storage adapter", workspaces);
        await this.removeFromStorageAdapter(workspaces);
        await asyncForEach(workspaces, async (name) => {
            const layout = await this.glue.layouts.get(name, "Global");
            if (!layout) {
                this.logger.warn(`data-migration - saveToStorageAdapter couldn't find layout "${name}"`);
                return;
            }
            const workspace = layoutToWorkspace(layout);
            this.logger.debug("data-migration - Saving workspace to storage adapter (see console for full workspace)", workspace.name);
            console.debug("Saving workspace to storage adapter", workspace);
            const { windowData, componentStates } = workspace;
            delete workspace.windowData;
            delete workspace.componentStates;
            await storageClient.save({ key: camelCase(workspace.name), topic: "finsemble.workspace", value: workspace });
            await asyncForEach(windowData, async (value) => {
                const key = camelCase(workspace.name, value.name);
                await storageClient.save({ key, topic: "finsemble.workspace", value });
            });
            await asyncForEach(Object.entries(componentStates), async ([componentName, value]) => {
                const key = camelCase(workspace.name, componentName, componentName);
                await storageClient.save({ key, topic: "finsemble.workspace", value });
            });
        });
        const { data: workspaceNames = [] } = await storageClient.getStandardized({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace" });
        const allWorkspaces = workspaceNames.concat(workspaces).filter(removeDups);
        await storageClient.save({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace", value: allWorkspaces });
        await this.setLastUsedWorkspaceName();
    }
    async removeFromStorageAdapter(workspaces) {
        const storageClient = this.storageClient;
        this.logger.debug("data-migration - Removing workspaces from storage adapter", workspaces);
        await asyncForEach(workspaces, async (workspace) => {
            const { data } = await storageClient.getStandardized({ key: camelCase(workspace), topic: "finsemble.workspace" });
            await asyncForEach(data?.windows ?? [], async (windowId) => {
                const componentStateKey = camelCase(workspace, windowId);
                const windowDataKey = camelCase(workspace, windowId, windowId);
                await storageClient.remove({ key: componentStateKey, topic: "finsemble.workspace" });
                await storageClient.remove({ key: windowDataKey, topic: "finsemble.workspace" });
            });
            await storageClient.remove({ key: camelCase(workspace), topic: "finsemble.workspace" });
        });
        const { data: workspaceNames = [] } = await storageClient.getStandardized({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace" });
        const allWorkspaces = workspaceNames.filter((name) => !workspaces.includes(name));
        await storageClient.save({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace", value: allWorkspaces });
        await this.setLastUsedWorkspaceName();
    }
    async setOrderInStorageAdapter(workspaces) {
        const storageClient = this.storageClient;
        this.logger.debug("data-migration - Setting workspace order in storage adapter", workspaces);
        await storageClient.save({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace", value: workspaces });
    }
    async renameInStorageAdapter(oldName, newName) {
        const storageClient = this.storageClient;
        this.logger.debug(`data-migration - Renaming workspace "${oldName}" to "${newName}" in storage adapter`);
        const { data } = await storageClient.getStandardized({ key: camelCase(oldName), topic: "finsemble.workspace" });
        await asyncForEach(data?.windows ?? [], async (windowId) => {
            const oldComponentStateKey = camelCase(oldName, windowId);
            const oldWindowDataKey = camelCase(oldName, windowId, windowId);
            const { data: componentState } = await storageClient.getStandardized({ key: oldComponentStateKey, topic: "finsemble.workspace" });
            const { data: windowData } = await storageClient.getStandardized({ key: oldWindowDataKey, topic: "finsemble.workspace" });
            await storageClient.remove({ key: oldComponentStateKey, topic: "finsemble.workspace" });
            await storageClient.remove({ key: oldWindowDataKey, topic: "finsemble.workspace" });
            const newComponentStateKey = camelCase(newName, windowId);
            const newWindowDataKey = camelCase(newName, windowId, windowId);
            await storageClient.save({ key: newComponentStateKey, topic: "finsemble.workspace", value: componentState });
            await storageClient.save({ key: newWindowDataKey, topic: "finsemble.workspace", value: windowData });
        });
        await storageClient.remove({ key: camelCase(oldName), topic: "finsemble.workspace" });
        data.name = newName;
        await storageClient.save({ key: camelCase(newName), topic: "finsemble.workspace", value: data });
        const { data: workspaceNames = [] } = await storageClient.getStandardized({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace" });
        const allWorkspaces = workspaceNames.map((name) => name === oldName ? newName : name);
        await storageClient.save({ key: "finsemble.allWorkspaces", topic: "finsemble.workspace", value: allWorkspaces });
        await this.setLastUsedWorkspaceName();
    }
    async setPreferences(value) {
        const storageClient = this.storageClient;
        this.logger.debug("data-migration - setting preferences in storage adapter", value);
        await storageClient.save({ key: "userPreferences", topic: "finsemble", value });
    }
    async storageAdapterPersistence(saveWorkspaces, savePreferences) {
        if (saveWorkspaces) {
            this.logger.debug("data-migration - Saving back to storage adapter is enabled at iocd.data.workspaces.saveToStorageAdapter");
        }
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.storageAdapter.save", safeInteropRegister(async ({ workspaces }, caller) => {
            if (!saveWorkspaces) {
                return;
            }
            await this.saveToStorageAdapter(workspaces);
        }, this.logger));
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.storageAdapter.remove", safeInteropRegister(async ({ workspaces }, caller) => {
            if (!saveWorkspaces) {
                return;
            }
            await this.removeFromStorageAdapter(workspaces);
        }, this.logger));
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.storageAdapter.order", safeInteropRegister(async ({ workspaces }, caller) => {
            if (!saveWorkspaces) {
                return;
            }
            await this.setOrderInStorageAdapter(workspaces);
        }, this.logger));
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.storageAdapter.rename", safeInteropRegister(async ({ oldName, newName }, caller) => {
            if (!saveWorkspaces) {
                return;
            }
            await this.renameInStorageAdapter(oldName, newName);
        }, this.logger));
        this.glue.prefs.subscribeFor("IO.Global.FinsemblePreferences", async (preference) => {
            this.logger.debug("data-migration - Updated IO.Global.FinsemblePreferences received", preference);
            if (!savePreferences) {
                return;
            }
            await this.setPreferences(preference.data);
        });
    }
    async run() {
        this.logger.debug("data-migration - Data migration start");
        const data = this.manifest.iocd?.data;
        this.logger.debug("data-migration - Migrating config", data);
        const workspacesLoad = data?.workspaces?.load ?? "storageAdapter";
        const workspacesSaveToStorageAdapter = data?.workspaces?.saveToStorageAdapter ?? true;
        const preferencesLoad = data?.preferences?.load ?? "storageAdapter";
        const advancedAppLauncherLoad = data?.advancedAppLauncher?.load ?? "storageAdapter";
        const preferencesSaveToStorageAdapter = data?.preferences?.saveToStorageAdapter ?? true;
        const asyncConstructors = new AsyncConstructors();
        this.storageClient = new StorageClientImpl(new Promise((resolve) => { resolve(this.glue); }), asyncConstructors, this.logger.loggerClient);
        const oneTimeConversionStatus = await this.glue.prefs.get("IO.Global.FinsembleOneTimeConversion");
        await this.maybeMigrateWorkspaces(workspacesLoad, oneTimeConversionStatus?.data ?? {});
        await this.maybeMigratePreferences(preferencesLoad, oneTimeConversionStatus?.data ?? {});
        await this.maybeMigrateAdvancedAppLauncher(advancedAppLauncherLoad, oneTimeConversionStatus?.data ?? {});
        await this.storageAdapterPersistence(workspacesSaveToStorageAdapter, preferencesSaveToStorageAdapter);
        this.logger.debug("data-migration - Data migration end");
    }
}
